import type { ReactNode } from "react"
import { Link, useRouterState } from "@tanstack/react-router"
import { Skeleton } from "@/components/ui/skeleton"
import { useWorkspaceState } from "../data/use-workspace-state"
import { workspaceBoundaryMode } from "./workspace-policy"

export function EmployerWorkspaceBoundary({ children }: { children: ReactNode }) {
  const workspace = useWorkspaceState()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const mode = workspaceBoundaryMode(workspace?.state, pathname)

  if (mode === "loading" || workspace === undefined) {
    return <Skeleton className="mx-auto h-48 w-full max-w-xl" />
  }

  if (mode === "passthrough") {
    return <>{children}</>
  }

  if (mode === "revoked" && workspace.state === "membership_revoked") {
    return (
      <div className="mkt-panel mkt-panel--wash mx-auto max-w-xl">
        <p className="mkt-panel__title">Company access revoked</p>
        <p className="mkt-panel__copy">
          You no longer have access to {workspace.formerCompany.name}. Company
          records and actions have been removed from this session.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to="/recruit/settings/account"
            className="home-wf-btn home-wf-btn--outline"
          >
            Personal account
          </Link>
          <Link to="/" className="home-wf-btn home-wf-btn--outline">
            Leave company desk
          </Link>
        </div>
      </div>
    )
  }

  if (mode === "no_company" && workspace.state === "no_company") {
    return (
      <div className="mkt-panel mkt-panel--wash mx-auto max-w-xl">
        <p className="mkt-panel__title">Create or join a company</p>
        <p className="mkt-panel__copy">
          Company records are available only after explicit onboarding or an
          accepted invitation. No company will be created by opening a desk page.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/onboarding/company" className="home-wf-btn home-wf-btn--lime">
            Create a company
          </Link>
          <Link to="/recruit/join" className="home-wf-btn home-wf-btn--outline">
            Join with an invite
          </Link>
        </div>
      </div>
    )
  }

  if (mode === "invited" && workspace.state === "membership_invited") {
    return (
      <div className="mkt-panel mkt-panel--wash mx-auto max-w-xl">
        <p className="mkt-panel__title">Company invitation pending</p>
        <p className="mkt-panel__copy">
          {workspace.company.name} invited this account. Open the invitation
          email and use its single-use link before accessing company records.
        </p>
        <Link
          to="/recruit/settings/account"
          className="home-wf-btn home-wf-btn--outline mt-4"
        >
          Personal account
        </Link>
      </div>
    )
  }

  if (mode === "migration_hold" && workspace.state === "migration_hold") {
    return (
      <div className="mkt-panel mkt-panel--wash mx-auto max-w-xl">
        <p className="mkt-panel__title">Workspace review in progress</p>
        <p className="mkt-panel__copy">{workspace.publicMessage}</p>
        <p className="mt-2 text-xs text-[color:var(--wf-mute)]">
          Support reference: {workspace.caseId}
        </p>
        <Link
          to="/recruit/settings/account"
          className="home-wf-btn home-wf-btn--outline mt-4"
        >
          Personal account
        </Link>
      </div>
    )
  }

  return null
}
